/**
 * VUKZ Brain — Frontend Service
 * Connects to /api/governance/brain/* endpoints
 */
import { AZURE } from './api';

// ─── Snapshots ───────────────────────────────────────────────────────────────

export const submitSnapshot = async (text, area = null, autoParse = true) => {
  const { data } = await AZURE.post('/governance/brain/snapshots', {
    text,
    area,
    auto_parse: autoParse,
  });
  return data;
};

export const getLatestSnapshot = async (area = 'global') => {
  const { data } = await AZURE.get('/governance/brain/snapshots/latest', {
    params: { area },
  });
  return data;
};

export const listSnapshots = async (limit = 20) => {
  const { data } = await AZURE.get('/governance/brain/snapshots', {
    params: { limit },
  });
  return data;
};

// ─── Memory ──────────────────────────────────────────────────────────────────

export const saveMemory = async ({ memoryType, title, content, sourceType = 'manual' }) => {
  const { data } = await AZURE.post('/governance/brain/memory', {
    memory_type: memoryType,
    title,
    content,
    source_type: sourceType,
  });
  return data;
};

export const getMemories = async (type = null, limit = 20) => {
  const { data } = await AZURE.get('/governance/brain/memory', {
    params: { type, limit },
  });
  return data;
};

export const deleteMemory = async (id) => {
  const { data } = await AZURE.delete(`/governance/brain/memory/${id}`);
  return data;
};

// ─── Tasks ───────────────────────────────────────────────────────────────────

export const createTask = async ({ title, area = 'backend', priority = 'P2', description = '' }) => {
  const { data } = await AZURE.post('/governance/brain/tasks', {
    title,
    area,
    priority,
    description,
  });
  return data;
};

export const getTasks = async ({ status, priority, area, limit = 50 } = {}) => {
  const { data } = await AZURE.get('/governance/brain/tasks', {
    params: { status, priority, area, limit },
  });
  return data;
};

export const updateTaskStatus = async (taskId, status) => {
  const { data } = await AZURE.patch(`/governance/brain/tasks/${taskId}/status`, { status });
  return data;
};

// ─── Decisions ───────────────────────────────────────────────────────────────

export const saveDecision = async ({ decision, rationale, impactArea, source = 'manual' }) => {
  const { data } = await AZURE.post('/governance/brain/decisions', {
    decision,
    rationale,
    impact_area: impactArea,
    source,
  });
  return data;
};

export const getDecisions = async (limit = 20) => {
  const { data } = await AZURE.get('/governance/brain/decisions', {
    params: { limit },
  });
  return data;
};

// ─── Brain Chat ──────────────────────────────────────────────────────────────

export const chatBrain = async (message, history = []) => {
  const { data } = await AZURE.post('/governance/brain/chat', {
    message,
    history,
  });
  return data;
};

// ─── GitHub Connector ────────────────────────────────────────────────────────

export const getGitHubSummary = async () => {
  const { data } = await AZURE.get('/governance/brain/github/summary');
  return data;
};

export const getGitHubCommits = async (repo = 'arenastats-backend', days = 7, limit = 10) => {
  const { data } = await AZURE.get('/governance/brain/github/commits', {
    params: { repo, days, limit },
  });
  return data;
};

export const getGitHubFile = async (repo, path) => {
  const { data } = await AZURE.get('/governance/brain/github/file', {
    params: { repo, path },
  });
  return data;
};

export const searchGitHubCode = async (q, repo = null) => {
  const { data } = await AZURE.get('/governance/brain/github/search', {
    params: { q, repo },
  });
  return data;
};

export const getGitHubStructure = async (repo = 'arenastats-backend', path = '', depth = 2) => {
  const { data } = await AZURE.get('/governance/brain/github/structure', {
    params: { repo, path, depth },
  });
  return data;
};

// ─── Azure Monitor ───────────────────────────────────────────────────────────

export const getAzureHealth = async () => {
  const { data } = await AZURE.get('/governance/brain/azure/health');
  return data;
};

export const getAzureCosts = async (days = 30) => {
  const { data } = await AZURE.get('/governance/brain/azure/costs', { params: { days } });
  return data;
};

export const getAzureDeployments = async (app = 'api-arenastats') => {
  const { data } = await AZURE.get('/governance/brain/azure/deployments', { params: { app } });
  return data;
};

// ─── Prompt Generator ────────────────────────────────────────────────────────

export const generatePrompt = async (task, context = null) => {
  const { data } = await AZURE.post('/governance/brain/generate-prompt', { task, context });
  return data;
};

// ─── Auto-Snapshot ───────────────────────────────────────────────────────────

export const triggerAutoSnapshot = async () => {
  const { data } = await AZURE.post('/governance/brain/auto-snapshot');
  return data;
};

export const getSnapshotDiff = async () => {
  const { data } = await AZURE.get('/governance/brain/snapshot-diff');
  return data;
};
